import { useEffect, useState, type ReactNode } from 'react';

import type { ToastVariantProp } from '@/components/ui/toast';

/**
 * Lightweight global toast store, modeled on the shadcn/ui pattern.
 *
 * Two entry points:
 *   - `toast(...)` — imperative, callable from non-React contexts (e.g. the
 *     TanStack Query `MutationCache.onError` handler).
 *   - `useToast()` — React hook for components that need to read or dismiss
 *     toasts (the `<Toaster />` renderer subscribes via this).
 *
 * Both share a single in-memory `state` and broadcast through `listeners`.
 * The viewport is mounted once at the app root, so anything in the React
 * tree (or outside it) can push a toast without prop-drilling.
 */

const TOAST_LIMIT = 3;
const TOAST_REMOVE_DELAY_MS = 5_000;

export interface ToastInput {
  readonly title?: ReactNode;
  readonly description?: ReactNode;
  readonly variant?: ToastVariantProp;
  /** Auto-dismiss after this many ms (default 5000). 0 disables auto-dismiss. */
  readonly durationMs?: number;
}

export interface Toast extends ToastInput {
  readonly id: string;
  readonly open: boolean;
}

interface State {
  readonly toasts: readonly Toast[];
}

type Action =
  | { type: 'ADD'; toast: Toast }
  | { type: 'DISMISS'; id?: string }
  | { type: 'REMOVE'; id?: string };

let state: State = { toasts: [] };
const listeners: ((s: State) => void)[] = [];
const removeTimers = new Map<string, ReturnType<typeof setTimeout>>();
let counter = 0;

function genId(): string {
  counter = (counter + 1) % Number.MAX_SAFE_INTEGER;
  return `toast-${String(counter)}`;
}

function reduce(s: State, action: Action): State {
  switch (action.type) {
    case 'ADD':
      return { toasts: [action.toast, ...s.toasts].slice(0, TOAST_LIMIT) };
    case 'DISMISS':
      return {
        toasts: s.toasts.map((t) =>
          action.id === undefined || t.id === action.id ? { ...t, open: false } : t,
        ),
      };
    case 'REMOVE':
      return {
        toasts: action.id === undefined ? [] : s.toasts.filter((t) => t.id !== action.id),
      };
  }
}

function dispatch(action: Action): void {
  state = reduce(state, action);
  for (const l of listeners) l(state);
}

function scheduleRemove(id: string): void {
  if (removeTimers.has(id)) return;
  const handle = setTimeout(() => {
    removeTimers.delete(id);
    dispatch({ type: 'REMOVE', id });
  }, 250);
  removeTimers.set(id, handle);
}

export function toast(input: ToastInput): { id: string; dismiss: () => void } {
  const id = genId();
  const next: Toast = {
    id,
    open: true,
    ...input,
  };
  dispatch({ type: 'ADD', toast: next });

  const duration = input.durationMs ?? TOAST_REMOVE_DELAY_MS;
  if (duration > 0) {
    setTimeout(() => {
      dispatch({ type: 'DISMISS', id });
      scheduleRemove(id);
    }, duration);
  }

  return {
    id,
    dismiss: () => {
      dispatch({ type: 'DISMISS', id });
      scheduleRemove(id);
    },
  };
}

export function useToast(): {
  toasts: readonly Toast[];
  toast: typeof toast;
  dismiss: (id?: string) => void;
} {
  const [snapshot, setSnapshot] = useState<State>(state);

  useEffect(() => {
    listeners.push(setSnapshot);
    return () => {
      const i = listeners.indexOf(setSnapshot);
      if (i >= 0) listeners.splice(i, 1);
    };
  }, []);

  return {
    toasts: snapshot.toasts,
    toast,
    dismiss: (id) => {
      dispatch(id === undefined ? { type: 'DISMISS' } : { type: 'DISMISS', id });
      if (id !== undefined) scheduleRemove(id);
    },
  };
}

/** Test helper — clears the store between specs. */
export function __resetToastsForTests(): void {
  for (const handle of removeTimers.values()) clearTimeout(handle);
  removeTimers.clear();
  state = { toasts: [] };
  for (const l of listeners) l(state);
}

/** Test helper — synchronous read of the current store state. */
export function __getToastsForTests(): readonly Toast[] {
  return state.toasts;
}
